import React from "react";
import { Link } from "react-router-dom";
import { buildTenantUserPath } from "./tenantPreview";

// Inline token grammar. Capture-group map (1-indexed):
//   1  full markdown link  [text](url)
//   2  link text
//   3  link url
//   4  **bold**            5 bold content
//   6  __bold__            7 bold content
//   8  *italic*            9 italic content
//   10 _italic_            11 italic content
//   12 `code`              13 code content
//   14 bare url (http(s):// | www.)
//   15 @mention boundary   ("" at start, or the preceding whitespace char)
//   16 @mention username   (letters/digits/underscore/dot/hyphen)
//
// The @mention alternative requires a leading boundary (start-of-string or
// whitespace) so emails like "a@b.com" never match — the "@" there is glued
// to a word char ("a"), so there is no boundary in front of it. The boundary
// char (group 15) is captured, not consumed semantically: it is re-emitted as
// plain text so surrounding spacing is preserved.
const INLINE_PATTERN =
  /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(\*\*([^*]+)\*\*)|(__(.+?)__)|(\*([^*]+)\*)|(_([^_]+)_)|(`([^`]+)`)|((?:https?:\/\/|www\.)[^\s<]+)|(^|\s)@([A-Za-z0-9_.-]+)/g;

function splitTrailingPunctuation(value) {
  const raw = `${value || ""}`;
  const match = raw.match(/[.,!?;:)]*$/);
  const trailing = match?.[0] || "";
  const core = trailing ? raw.slice(0, -trailing.length) : raw;
  return { core, trailing };
}

function normalizeHref(value) {
  return value.startsWith("www.") ? `https://${value}` : value;
}

function renderInline(text, keyPrefix) {
  const safeText = `${text || ""}`;
  const parts = [];
  let lastIndex = 0;
  let match;
  let tokenIndex = 0;

  INLINE_PATTERN.lastIndex = 0;
  while ((match = INLINE_PATTERN.exec(safeText)) !== null) {
    if (match.index > lastIndex) {
      parts.push(safeText.slice(lastIndex, match.index));
    }

    if (match[2] && match[3]) {
      parts.push(
        <a
          key={`${keyPrefix}-link-${tokenIndex}`}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
        >
          {match[2]}
        </a>
      );
    } else if (match[5]) {
      parts.push(
        <strong key={`${keyPrefix}-bold-${tokenIndex}`}>{match[5]}</strong>
      );
    } else if (match[7]) {
      parts.push(
        <strong key={`${keyPrefix}-bold2-${tokenIndex}`}>{match[7]}</strong>
      );
    } else if (match[9]) {
      parts.push(<em key={`${keyPrefix}-italic-${tokenIndex}`}>{match[9]}</em>);
    } else if (match[11]) {
      parts.push(
        <em key={`${keyPrefix}-italic2-${tokenIndex}`}>{match[11]}</em>
      );
    } else if (match[13]) {
      parts.push(<code key={`${keyPrefix}-code-${tokenIndex}`}>{match[13]}</code>);
    } else if (match[14]) {
      const { core, trailing } = splitTrailingPunctuation(match[14]);
      if (core) {
        parts.push(
          <a
            key={`${keyPrefix}-plain-link-${tokenIndex}`}
            href={normalizeHref(core)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            {core}
          </a>
        );
      }
      if (trailing) {
        parts.push(trailing);
      }
    } else if (match[16]) {
      // @mention — re-emit the captured boundary (whitespace) as plain text,
      // then render the username as a tenant-aware profile link chip.
      const boundary = match[15] || "";
      if (boundary) {
        parts.push(boundary);
      }
      const username = match[16];
      parts.push(
        <Link
          key={`${keyPrefix}-mention-${tokenIndex}`}
          to={buildTenantUserPath(username)}
          className="rich-mention"
          style={{
            color: "var(--primary, #ec166e)",
            fontWeight: 600,
            textDecoration: "none",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          @{username}
        </Link>
      );
    } else {
      parts.push(match[0]);
    }

    tokenIndex += 1;
    lastIndex = INLINE_PATTERN.lastIndex;
  }

  if (lastIndex < safeText.length) {
    parts.push(safeText.slice(lastIndex));
  }

  return parts;
}

export function renderInlineRichText(text, keyPrefix = "rich-inline") {
  const lines = `${text || ""}`.split("\n");
  const lineParts = [];

  lines.forEach((line, lineIndex) => {
    lineParts.push(...renderInline(line, `${keyPrefix}-${lineIndex}`));
    if (lineIndex < lines.length - 1) {
      lineParts.push(<br key={`${keyPrefix}-${lineIndex}-br`} />);
    }
  });

  return lineParts;
}

export function renderRichText(text, keyPrefix = "rich-text") {
  const paragraphs = `${text || ""}`.split(/\n{2,}/).filter(Boolean);
  if (paragraphs.length === 0) return null;

  return paragraphs.map((paragraph, paragraphIndex) => {
    const lines = paragraph.split("\n");
    const lineParts = [];

    lines.forEach((line, lineIndex) => {
      lineParts.push(
        ...renderInline(line, `${keyPrefix}-${paragraphIndex}-${lineIndex}`)
      );
      if (lineIndex < lines.length - 1) {
        lineParts.push(
          <br key={`${keyPrefix}-${paragraphIndex}-${lineIndex}-br`} />
        );
      }
    });

    return (
      <p key={`${keyPrefix}-${paragraphIndex}`} style={{ marginBottom: 12 }}>
        {lineParts}
      </p>
    );
  });
}
